import React, { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import {
  Animated,
  View,
  FlatList,
  TouchableOpacity,
  Pressable,
  StyleSheet,
  Platform,
  Modal,
  ScrollView,
} from 'react-native';
import { Text } from '@/components/common/text';
import { MessageBubble, FileBubble } from './MessageBubble';
import { ChatWindowSkeleton } from './ChatWindowSkeleton';
import { useDelayedFlag } from '@/hooks/common/useDelayedFlag';
import { FileUploadSection, UploadFile } from '@/components/common/admin/mobile/FileUploadSection';
import { useConversation, useConversationUtils } from '@/hooks/admin/useMessages';
import { useSendMessageMutation, useMarkConversationAsReadMutation } from '@/store/api/admin/messagesApi';
import { IChatWindowProps, IMessage } from '@/types/admin/messages';
import { SVG_COMPONENTS } from '@/assets/admin';
import { PERFORMANCE_CONSTANTS } from '@/constants/admin/performance';
import { generateMessageGroupKey, generateFileKey, validateUniqueKeys } from '@/utils/admin/keyUtils';
import { useErrorToast } from '@/contexts/admin/ToastContext';
import { groupMessages, MessageGroup } from '@/utils/residents/messages/groupMessages';
import { useAutoScrollToBottom } from '@/hooks/residents/useAutoScrollToBottom';
import { useAdminColors } from '@/hooks/admin/useAdminColors';
import { GlassCard } from '@/components/shared/redesign';
import { GlassFormInput } from '@/components/admin/redesign';

/** A wait shorter than this never shows the skeleton — it would only flash. */
const SKELETON_DELAY_MS = 120;
/** Fade for the message list once it has jumped to the latest message. */
const LIST_FADE_MS = 150;
/** Never leave the list hidden if the content-size callback does not come. */
const REVEAL_FALLBACK_MS = 300;
/**
 * Web: a pressable takes focus from the textarea on mousedown, which closes the
 * on-screen keyboard on every send and drops the caret. Cancelling the default
 * keeps the composer focused; the press itself still fires. Native ignores it.
 */
const keepComposerFocusProps = {
  onMouseDown: (event: { preventDefault: () => void }) => event.preventDefault(),
} as object;

// The app's tsconfig has no DOM lib (see utils/common/alert.ts).
declare const window: any;

export const ChatWindow: React.FC<IChatWindowProps> = ({
  conversationId,
}) => {
  const { redesign, semantic, admin } = useAdminColors();
  const { conversation, isLoading, isUninitialized, error } = useConversation(conversationId);
  // A freshly mounted window (desktop mounts one per conversation) is
  // uninitialized for a render before its request fires: that is loading,
  // not "no conversation", so it must not flash the empty state.
  const isPending = isLoading || (isUninitialized && !!conversationId);
  const showSkeleton = useDelayedFlag(isPending, SKELETON_DELAY_MS);
  const { formatMessageDate } = useConversationUtils();
  const [sendMessage, { isLoading: isSending }] = useSendMessageMutation();
  const [markAsRead] = useMarkConversationAsReadMutation();

  const [messageText, setMessageText] = useState('');
  const [attachedFiles, setAttachedFiles] = useState<UploadFile[]>([]);
  const [showFileUpload, setShowFileUpload] = useState(false);
  const { showApiError } = useErrorToast();

  // Mark conversation as read once per open so the unread dot clears on view.
  const hasMarkedAsReadRef = useRef<number | null>(null);
  useEffect(() => {
    if (!conversationId || !conversation) return;
    if (hasMarkedAsReadRef.current === conversationId) return;
    hasMarkedAsReadRef.current = conversationId;
    markAsRead(conversationId).catch(() => {
      hasMarkedAsReadRef.current = null;
    });
  }, [conversationId, conversation, markAsRead]);

  // Use the shared auto-scroll hook for keyboard-aware scrolling
  const {
    listRef: flatListRef,
    isNearBottomRef,
    handleScroll,
  } = useAutoScrollToBottom<FlatList>();

  // Latest content height reported by the list. FlatList.scrollToEnd estimates
  // the offset of an unmeasured last cell from the average cell length, so on a
  // thread whose "today" group is tall it lands short — and that short landing
  // made the scroll handler decide the user was no longer near the bottom,
  // gating every later jump off. Scrolling to the content height itself is
  // exact on every platform: the scroll view clamps it to the true end.
  const contentHeightRef = useRef(0);
  // Set when the user's own message was sent: the next content growth is the
  // refetched thread containing it, and it must be brought into view even if a
  // scroll event has since flagged the list as not near the bottom.
  const scrollOnNextGrowthRef = useRef(false);

  const jumpToEnd = useCallback((animated: boolean) => {
    const offset = contentHeightRef.current;
    if (offset > 0) {
      flatListRef.current?.scrollToOffset({ offset, animated });
    } else {
      flatListRef.current?.scrollToEnd({ animated });
    }
    isNearBottomRef.current = true;
  }, [flatListRef, isNearBottomRef]);

  const jumpToEndIfNearBottom = useCallback((animated: boolean) => {
    if (isNearBottomRef.current) {
      jumpToEnd(animated);
    }
  }, [isNearBottomRef, jumpToEnd]);

  // Group messages by date using the shared utility
  const messageGroups = useMemo(() => {
    const result = groupMessages(conversation?.messages, {
      getHeaderDate: (msg) => formatMessageDate(msg.createdAt),
      getTimestamp: (msg) => msg.createdAt,
    }) as MessageGroup<IMessage>[];

    // Validate unique keys in development
    validateUniqueKeys(
      result,
      (item) => generateMessageGroupKey(item.date),
      'messageGroups'
    );

    return result;
  }, [conversation?.messages, formatMessageDate]);

  // On web the on-screen keyboard arrives as a visual-viewport resize (the
  // screen shrinks to it, see useWebKeyboardViewport); re-anchor the list to
  // its end then. RN's Keyboard events never fire on web.
  useEffect(() => {
    if (Platform.OS !== 'web') return undefined;
    const vv = typeof window === 'undefined' ? undefined : (window as any).visualViewport;
    if (!vv) return undefined;
    const onResize = () => jumpToEndIfNearBottom(false);
    vv.addEventListener('resize', onResize);
    return () => vv.removeEventListener('resize', onResize);
  }, [jumpToEndIfNearBottom]);

  // Auto-scroll to bottom when new messages arrive
  // Keyboard events are handled by the useAutoScrollToBottom hook
  useEffect(() => {
    let timeoutId: ReturnType<typeof setTimeout> | undefined;
    if (messageGroups.length > 0) {
      timeoutId = setTimeout(() => {
        jumpToEndIfNearBottom(false);
      }, 100);
    }
    return () => {
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [messageGroups, jumpToEndIfNearBottom]);

  // The list mounts scrolled to the top and only jumps to the latest message
  // once its content is sized. Keep it invisible until then and fade it in, so
  // a conversation opens on its newest messages instead of jumping to them.
  const listOpacity = useRef(new Animated.Value(0)).current;
  const listRevealedRef = useRef(false);
  const revealList = useCallback(() => {
    if (listRevealedRef.current) return;
    listRevealedRef.current = true;
    Animated.timing(listOpacity, {
      toValue: 1,
      duration: LIST_FADE_MS,
      // Web has no native driver; natively it keeps the fade off the busy JS thread.
      useNativeDriver: Platform.OS !== 'web',
    }).start();
  }, [listOpacity]);

  // An empty conversation has nothing to size; otherwise fall back to a timer
  // so the list can never stay hidden.
  useEffect(() => {
    if (!conversation) return undefined;
    if (messageGroups.length === 0) {
      revealList();
      return undefined;
    }
    const handle = setTimeout(revealList, REVEAL_FALLBACK_MS);
    return () => clearTimeout(handle);
  }, [conversation, messageGroups.length, revealList]);

  const handleSendMessage = useCallback(async () => {
    if (!messageText.trim() && attachedFiles.length === 0) return;

    const formData = new FormData();
    formData.append('content', messageText.trim());
    formData.append('isAdmin', 'true');

    // Add files to form data (UploadFile format)
    // On web, file.file contains the actual File object
    // On native, we need { uri, type, name } format
    attachedFiles.forEach((file) => {
      if (file.file) {
        // Web: use the actual File object (already contains filename)
        formData.append('files', file.file);
      } else if (file.uri) {
        // Native: use the { uri, type, name } format
        formData.append('files', {
          uri: file.uri,
          type: file.type,
          name: file.name,
        } as any);
      }
    });

    try {
      await sendMessage({
        conversationId,
        body: formData,
      }).unwrap();
      setMessageText('');
      setAttachedFiles([]);
      // The sent message only shows up with the invalidated conversation
      // refetch, i.e. on the list's next content-size change — jump then.
      scrollOnNextGrowthRef.current = true;
    } catch (error: any) {
      showApiError(error?.data?.message || 'Failed to send message');
    }
  }, [attachedFiles, conversationId, messageText, sendMessage, showApiError]);

  const renderMessageGroup = useCallback(({ item }: { item: MessageGroup<IMessage> }) => (
    <View style={styles.messageGroup}>
      {/* Date header */}
      <View style={styles.dateHeader}>
        <Text
          style={[
            styles.dateText,
            {
              color: redesign.text.subtle,
              backgroundColor: redesign.card.bg,
            },
          ]}
        >
          {item.date}
        </Text>
      </View>

      {/* Messages */}
      {item.messages.map((message) => (
        <View key={message.id}>
          {/* Text message */}
          {message.content?.length > 0 && (
            <MessageBubble message={message} />
          )}

          {/* File attachments */}
          {message.files?.map((file, index) => (
            <FileBubble
              key={generateFileKey(message.id, file, index)}
              file={file}
              isAdmin={message.isAdmin}
            />
          ))}
        </View>
      ))}
    </View>
  ), [redesign.text.subtle, redesign.card.bg]);

  const renderMessageInput = useCallback(() => (
    <View
      style={[
        styles.inputContainer,
        {
          backgroundColor: redesign.bg.base,
          borderTopColor: redesign.card.border,
        },
      ]}
    >
      {/* File attachments preview */}
      {attachedFiles.length > 0 && (
        <View style={styles.attachmentsPreview}>
          {attachedFiles.map((file, index) => (
            <View
              key={file.id || index}
              style={[styles.attachmentItem, { backgroundColor: redesign.card.bg }]}
            >
              <SVG_COMPONENTS.PAPERCLIP
                width={16}
                height={16}
                color={redesign.text.muted}
              />
              <Text style={[styles.attachmentName, { color: redesign.text.primary }]}>
                {file.name || 'File'}
              </Text>
              <TouchableOpacity
                onPress={() => {
                  setAttachedFiles(files => files.filter((_, i) => i !== index));
                }}
                style={styles.removeAttachment}
              >
                <SVG_COMPONENTS.X_CIRCLE
                  width={16}
                  height={16}
                  color={semantic.status.error}
                />
              </TouchableOpacity>
            </View>
          ))}
        </View>
      )}

      <GlassCard padding={8} style={styles.inputCard}>
        <View style={styles.inputRow}>
          <TouchableOpacity
            style={styles.attachButton}
            onPress={() => setShowFileUpload(true)}
          >
            <SVG_COMPONENTS.PAPERCLIP
              width={20}
              height={20}
              color={redesign.text.muted}
            />
          </TouchableOpacity>

          <GlassFormInput
            value={messageText}
            onChangeText={setMessageText}
            placeholder="Your message..."
            multiline
            maxLength={1000}
            autoFocus={false}
            blurOnSubmit={false}
            returnKeyType="default"
            keyboardType="default"
            textAlignVertical="center"
            onFocus={() => jumpToEnd(false)}
            containerStyle={styles.textInputContainer}
            inputStyle={styles.textInput}
          />

          <TouchableOpacity
            style={[
              styles.sendButton,
              { backgroundColor: redesign.card.bg },
              (messageText.trim() || attachedFiles.length > 0) && {
                backgroundColor: admin.formInput.borderFocused,
              },
            ]}
            onPress={handleSendMessage}
            disabled={isSending || (!messageText.trim() && attachedFiles.length === 0)}
            {...keepComposerFocusProps}
          >
            <Text
              style={[
                styles.sendButtonText,
                {
                  color:
                    messageText.trim() || attachedFiles.length > 0
                      ? '#FFFFFF'
                      : redesign.text.primary,
                },
              ]}
            >
              Send
            </Text>
          </TouchableOpacity>
        </View>
      </GlassCard>
    </View>
  ), [
    admin.formInput.borderFocused,
    attachedFiles,
    handleSendMessage,
    isSending,
    jumpToEnd,
    messageText,
    redesign.bg.base,
    redesign.card.bg,
    redesign.card.border,
    redesign.text.muted,
    redesign.text.primary,
    semantic.status.error,
  ]);

  if (isPending) {
    return showSkeleton ? (
      <ChatWindowSkeleton />
    ) : (
      <View style={[styles.container, { backgroundColor: redesign.bg.base }]} />
    );
  }

  if (error) {
    return (
      <View style={styles.errorContainer}>
        <SVG_COMPONENTS.X_CIRCLE
          width={48}
          height={48}
          color={semantic.status.error}
        />
        <Text style={[styles.errorText, { color: redesign.text.muted }]}>
          Failed to load conversation
        </Text>
      </View>
    );
  }

  if (!conversation) {
    return (
      <View style={styles.emptyContainer}>
        <SVG_COMPONENTS.EMPTY_MESSAGE
          width={64}
          height={64}
          color={redesign.text.subtle}
        />
        <Text style={[styles.emptyText, { color: redesign.text.muted }]}>
          Select a conversation to start messaging
        </Text>
      </View>
    );
  }

  return (
    <View style={[styles.container, { backgroundColor: redesign.bg.base }]}>
      <Animated.View style={[styles.listWrap, { opacity: listOpacity }]}>
      <FlatList
        ref={flatListRef}
        data={messageGroups}
        renderItem={renderMessageGroup}
        keyExtractor={(item) => generateMessageGroupKey(item.date)}
        style={styles.messagesList}
        contentContainerStyle={styles.messagesContent}
        showsVerticalScrollIndicator={false}
        keyboardShouldPersistTaps="handled"
        keyboardDismissMode="none"
        scrollEventThrottle={16}
        // Performance optimizations
        removeClippedSubviews={true}
        maxToRenderPerBatch={PERFORMANCE_CONSTANTS.MAX_TO_RENDER_PER_BATCH}
        windowSize={PERFORMANCE_CONSTANTS.WINDOW_SIZE}
        initialNumToRender={PERFORMANCE_CONSTANTS.INITIAL_NUM_TO_RENDER}
        updateCellsBatchingPeriod={100}
        onScroll={(e) => {
          const { contentOffset, layoutMeasurement, contentSize } = e.nativeEvent;
          handleScroll(contentOffset.y, layoutMeasurement.height, contentSize.height, 24);
        }}
        onContentSizeChange={(_width, height) => {
          contentHeightRef.current = height;
          if (scrollOnNextGrowthRef.current || isNearBottomRef.current) {
            scrollOnNextGrowthRef.current = false;
            jumpToEnd(false);
          }
          revealList();
        }}
        onLayout={() => {
          // Scroll to bottom when layout changes (e.g., keyboard appears)
          setTimeout(() => {
            jumpToEndIfNearBottom(true);
          }, 100);
        }}
      />
      </Animated.View>
      {renderMessageInput()}

      {/* File Upload Modal - Desktop */}
      {Platform.OS === 'web' && showFileUpload && (
        <Pressable
          style={styles.desktopModalOverlay}
          onPress={() => setShowFileUpload(false)}
        >
          <Pressable
            style={styles.desktopModalContent}
            onPress={(e) => e.stopPropagation()} // Prevent closing when clicking inside
          >
            <GlassCard padding={0} style={styles.desktopModalCard}>
              <View
                style={[
                  styles.desktopModalHeader,
                  { borderBottomColor: redesign.card.border },
                ]}
              >
                <Text
                  style={[styles.desktopModalTitle, { color: redesign.text.primary }]}
                >
                  Add Attachments
                </Text>
                <TouchableOpacity
                  style={styles.desktopModalCloseButton}
                  onPress={() => setShowFileUpload(false)}
                >
                  <SVG_COMPONENTS.X_CIRCLE
                    width={24}
                    height={24}
                    color={redesign.text.muted}
                  />
                </TouchableOpacity>
              </View>

              <ScrollView style={styles.desktopModalBody}>
                <FileUploadSection
                  title="Attachments"
                  subtitle="Add files to your message"
                  files={attachedFiles}
                  onFilesChange={setAttachedFiles}
                  maxFiles={5}
                  allowedTypes={['image', 'pdf', 'document', 'text']}
                />
              </ScrollView>

              <View
                style={[
                  styles.desktopModalFooter,
                  { borderTopColor: redesign.card.border },
                ]}
              >
                <TouchableOpacity
                  style={[
                    styles.desktopModalCancelButton,
                    {
                      backgroundColor: redesign.card.bg,
                      borderColor: redesign.card.border,
                    },
                  ]}
                  onPress={() => setShowFileUpload(false)}
                >
                  <Text style={[styles.desktopModalCancelText, { color: redesign.text.primary }]}>
                    Cancel
                  </Text>
                </TouchableOpacity>
                <TouchableOpacity
                  style={[
                    styles.desktopModalDoneButton,
                    { backgroundColor: admin.formInput.borderFocused },
                  ]}
                  onPress={() => setShowFileUpload(false)}
                >
                  <Text style={styles.desktopModalDoneText}>Done</Text>
                </TouchableOpacity>
              </View>
            </GlassCard>
          </Pressable>
        </Pressable>
      )}

      {/* File Upload Modal - Mobile */}
      {Platform.OS !== 'web' && (
        <Modal
          visible={showFileUpload}
          animationType="slide"
          presentationStyle="pageSheet"
          onRequestClose={() => setShowFileUpload(false)}
        >
          <View style={[styles.fileUploadModal, { backgroundColor: redesign.bg.base }]}>
            <View
              style={[
                styles.fileUploadHeader,
                {
                  backgroundColor: redesign.bg.base,
                  borderBottomColor: redesign.card.border,
                },
              ]}
            >
              <TouchableOpacity
                style={styles.fileUploadBackButton}
                onPress={() => setShowFileUpload(false)}
              >
                <SVG_COMPONENTS.CHEVRON_LEFT
                  width={24}
                  height={24}
                  color={redesign.text.primary}
                />
              </TouchableOpacity>

              <Text style={[styles.fileUploadTitle, { color: redesign.text.primary }]}>
                Add Attachments
              </Text>

              <TouchableOpacity
                style={styles.fileUploadDoneButton}
                onPress={() => setShowFileUpload(false)}
              >
                <Text style={[styles.fileUploadDoneText, { color: semantic.text.link }]}>
                  Done
                </Text>
              </TouchableOpacity>
            </View>

            <FileUploadSection
              title="Attachments"
              subtitle="Add files to your message"
              files={attachedFiles}
              onFilesChange={setAttachedFiles}
              maxFiles={5}
              allowedTypes={['image', 'pdf', 'document', 'text']}
            />
          </View>
        </Modal>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  errorContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 24,
  },
  errorText: {
    fontSize: 16,
    marginTop: 12,
    textAlign: 'center',
  },
  emptyContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 24,
  },
  emptyText: {
    fontSize: 16,
    marginTop: 16,
    textAlign: 'center',
  },
  messagesList: {
    flex: 1,
  },
  listWrap: {
    flex: 1,
  },
  messagesContent: {
    paddingVertical: 16,
    paddingBottom: 20,
  },
  messageGroup: {
    marginBottom: 16,
  },
  dateHeader: {
    alignItems: 'center',
    marginVertical: 16,
  },
  dateText: {
    fontSize: 12,
    paddingHorizontal: 12,
    paddingVertical: 4,
    borderRadius: 12,
  },
  inputContainer: {
    borderTopWidth: 1,
    paddingHorizontal: 16,
    paddingVertical: 12,
  },
  attachmentsPreview: {
    marginBottom: 12,
  },
  attachmentItem: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 8,
    marginBottom: 4,
  },
  attachmentName: {
    flex: 1,
    fontSize: 14,
    marginLeft: 8,
  },
  removeAttachment: {
    marginLeft: 8,
  },
  inputCard: {
    padding: 8,
  },
  inputRow: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  attachButton: {
    padding: 8,
    marginRight: 8,
  },
  textInputContainer: {
    flex: 1,
    marginBottom: 0,
    marginRight: 8,
  },
  textInput: {
    minHeight: 44,
    maxHeight: 100,
    paddingTop: 10,
    paddingBottom: 10,
  },
  sendButton: {
    paddingHorizontal: 16,
    borderRadius: 20,
    minWidth: 60,
    height: 44,
    alignItems: 'center',
    justifyContent: 'center',
  },
  sendButtonText: {
    fontSize: 14,
    fontWeight: '600',
  },
  // File Upload Modal Styles
  fileUploadModal: {
    flex: 1,
  },
  fileUploadHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderBottomWidth: 1,
    paddingTop: 20,
  },
  fileUploadBackButton: {
    padding: 8,
    marginRight: 8,
  },
  fileUploadTitle: {
    flex: 1,
    fontSize: 18,
    fontWeight: '600',
    textAlign: 'center',
  },
  fileUploadDoneButton: {
    padding: 8,
    marginLeft: 8,
  },
  fileUploadDoneText: {
    fontSize: 16,
    fontWeight: '600',
  },
  // Desktop Modal Styles
  desktopModalOverlay: {
    position: 'absolute' as any,
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 1000,
  },
  desktopModalContent: {
    width: '90%',
    maxWidth: 500,
    maxHeight: '80%',
  },
  desktopModalCard: {
    overflow: 'hidden',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.25,
    shadowRadius: 12,
    elevation: 10,
  },
  desktopModalHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 20,
    paddingVertical: 16,
    borderBottomWidth: 1,
  },
  desktopModalTitle: {
    fontSize: 18,
    fontWeight: '600',
  },
  desktopModalCloseButton: {
    padding: 4,
  },
  desktopModalBody: {
    padding: 20,
    maxHeight: 400,
  },
  desktopModalFooter: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    alignItems: 'center',
    paddingHorizontal: 20,
    paddingVertical: 16,
    borderTopWidth: 1,
    gap: 12,
  },
  desktopModalCancelButton: {
    paddingHorizontal: 16,
    paddingVertical: 10,
    borderRadius: 8,
    borderWidth: 1,
  },
  desktopModalCancelText: {
    fontSize: 14,
    fontWeight: '600',
  },
  desktopModalDoneButton: {
    paddingHorizontal: 16,
    paddingVertical: 10,
    borderRadius: 8,
  },
  desktopModalDoneText: {
    fontSize: 14,
    fontWeight: '600',
    color: '#FFFFFF',
  },
});
